import { useCallback } from 'react';

import retry from 'async-retry';

import { ELogLevel } from '@services/errorService';

import { useErrorContext } from '../../errorService';
import { EXPONENTIAL_GROWTH_FACTOR, MINIMAL_TIMEOUT_BETWEEN_REQUESTS, NUMBER_OF_RETRIES } from '../constants';
import { useHttpClientContext } from '../HttpClientContext';
import {
  AxiosErrorExtended,
  EHttpClient,
  IHttpClientError,
  IHttpClientResponseAwaitable,
  IRequestParam,
  IUseHttpClientParams,
} from '../model';

import { createErrorInstance, handleError, isFaultTolerantError, isLogWorthy } from './utils/utils';

export const useHttpClientAwaitable = <TResult, TParam, TRequestResult = TResult>({
  method,
  path,
  mappingFunction,
  customHeaders,
  httpClientType = EHttpClient.CHECKOUT_GATEWAY,
  requestRetryCount = NUMBER_OF_RETRIES,
}: IUseHttpClientParams<TResult, TRequestResult>): IHttpClientResponseAwaitable<TResult, TParam> => {
  const { callHttpClient, callParfumsHttpClient } = useHttpClientContext();
  const { callLogger } = useErrorContext();

  const httpClient = httpClientType === EHttpClient.CHECKOUT_GATEWAY ? callHttpClient : callParfumsHttpClient;

  const request = useCallback(
    async (params: IRequestParam<TParam> = {}): Promise<TResult | null> => {
      const { data, restUrlParams, queryUrlParams, cancelToken } = params;
      let lastErrorCaught: IHttpClientError | null = null;

      try {
        const result = await retry(
          (bail) => {
            if (lastErrorCaught && !isFaultTolerantError(lastErrorCaught)) {
              bail(lastErrorCaught);
              lastErrorCaught = null;
              return null;
            }

            lastErrorCaught = null;

            return httpClient<TRequestResult>({
              method,
              path,
              data,
              customHeaders,
              queryUrlParams,
              restUrlParams,
              cancelToken,
            });
          },
          {
            onRetry: (error: Error) => {
              lastErrorCaught = handleError(error as AxiosErrorExtended);
            },
            retries: requestRetryCount,
            factor: EXPONENTIAL_GROWTH_FACTOR,
            minTimeout: MINIMAL_TIMEOUT_BETWEEN_REQUESTS,
          },
        );

        if (!result?.data) {
          return null;
        }

        if (typeof mappingFunction === 'function') {
          return mappingFunction(result.data, callLogger);
        }

        return result.data as unknown as TResult;
      } catch (error) {
        const mappedError = handleError(error as AxiosErrorExtended);

        isLogWorthy(mappedError) &&
          callLogger({ logLevel: ELogLevel.ERROR, logError: createErrorInstance(mappedError) });

        return null;
      }
    },
    [httpClient, callLogger, method, path, customHeaders, mappingFunction, requestRetryCount],
  );

  return { request };
};
